import { useCallback, useState } from 'react';

import { usePermissions } from '@/hooks/usePermissions';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';

import { useActiveCampaignStatus } from '../hooks/useActiveCampaignStatus';
import { useAvailableContactProperties } from '../hooks/useAvailableContactProperties';
import { DisconnectConfirmation } from './DisconnectConfirmation';
import { FieldMappingManager } from './FieldMappingManager';
import { SyncHealthPanel } from './SyncHealthPanel';

interface ConnectionStatusPanelProps {
  orgId: number | string;
}

export function ConnectionStatusPanel({ orgId }: ConnectionStatusPanelProps) {
  const { data, isLoading, isError } = useActiveCampaignStatus(orgId);
  const { hasPermissionGroup } = usePermissions();
  const [isDisconnectOpen, setIsDisconnectOpen] = useState(false);
  const [managingMappings, setManagingMappings] = useState(false);

  // Contact-property schema is only needed to render the field-mapping UI, so
  // don't fetch it on every mount. Gate on connected + the mapping view being
  // open; passing undefined disables the underlying schema query.
  const shouldLoadProperties = Boolean(data?.connected) && managingMappings;
  const availableAhoyProperties = useAvailableContactProperties(
    shouldLoadProperties ? orgId : undefined
  );

  const handleDisconnectClick = useCallback(() => {
    setIsDisconnectOpen(true);
  }, []);

  const handleDisconnectClose = useCallback(() => {
    setIsDisconnectOpen(false);
  }, []);

  const handleEditMappings = useCallback(() => {
    setManagingMappings(true);
  }, []);

  const handleBackFromMappings = useCallback(() => {
    setManagingMappings(false);
  }, []);

  // Only show the hard error state when we have no cached status to fall back
  // on. React Query keeps the last successful `data` when a background poll
  // fails, so a transient failure shouldn't wipe an already-connected account's
  // status and actions — render the last known state through instead.
  if (isError && !data) {
    return (
      <div className='rounded-lg border border-utility-error-200 bg-utility-error-50 p-4'>
        <p className='text-sm font-semibold text-utility-error-900'>
          Couldn&apos;t load ActiveCampaign status
        </p>
        <p className='mt-1 text-sm text-utility-error-700'>
          Something went wrong while checking your connection. Please try again.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingIndicator size='sm' />;
  }

  if (!data?.connected) {
    return null;
  }

  const isAdmin = hasPermissionGroup('ahoy_other_admin');

  if (managingMappings) {
    return (
      <div className='card-refined p-6'>
        <FieldMappingManager
          orgId={orgId}
          availableAhoyProperties={availableAhoyProperties}
          onBack={handleBackFromMappings}
        />
      </div>
    );
  }

  const isSyncing = data.bulk_completed_at == null;
  // Reconciliation and webhooks each advance sync independently, so "Last
  // activity" is the most recent of the two — not whichever we check first.
  const activityTimestamps = [
    data.last_reconciled_at,
    data.last_webhook_received_at,
  ].filter((value): value is string => value != null);
  const lastActivityAt =
    activityTimestamps.length > 0
      ? activityTimestamps.reduce((latest, current) =>
          new Date(current).getTime() > new Date(latest).getTime()
            ? current
            : latest
        )
      : null;
  const lastActivityText = lastActivityAt
    ? new Date(lastActivityAt).toLocaleString()
    : null;

  return (
    <div className='card-refined p-6'>
      <div className='flex items-start justify-between gap-4'>
        <div className='flex-1 min-w-0'>
          <p className='text-xs font-medium uppercase tracking-wide text-[var(--color-text-tertiary)]'>
            ActiveCampaign account
          </p>
          <h3 className='text-heading-3 text-[var(--color-text-primary)] truncate'>
            {data.account_id ?? 'Connected'}
          </h3>
          {data.default_list_name && (
            <p className='mt-1 text-body-small text-[var(--color-text-secondary)]'>
              Default list: {data.default_list_name}
            </p>
          )}
          {typeof data.total_contacts_synced === 'number' && (
            <p className='mt-1 text-body-small text-[var(--color-text-secondary)]'>
              {data.total_contacts_synced.toLocaleString()} contacts synced
            </p>
          )}
          {lastActivityText && (
            <p className='mt-1 text-body-small text-[var(--color-text-secondary)]'>
              Last activity {lastActivityText}
            </p>
          )}
        </div>
        {isAdmin && (
          <div className='flex shrink-0 items-center gap-2'>
            <Button color='secondary' size='sm' onClick={handleEditMappings}>
              Edit field mappings
            </Button>
            <Button color='secondary' size='sm' onClick={handleDisconnectClick}>
              Disconnect
            </Button>
          </div>
        )}
      </div>

      <div className='mt-4'>
        {isSyncing ? (
          <div className='flex items-center gap-2'>
            <LoadingIndicator size='xs' inline />
            <span className='text-body-small text-[var(--color-text-secondary)]'>
              Initial import in progress…
            </span>
          </div>
        ) : (
          <Badge type='pill-color' color='success'>
            Initial import complete
          </Badge>
        )}
      </div>

      <SyncHealthPanel orgId={orgId} />

      <DisconnectConfirmation
        orgId={orgId}
        isOpen={isDisconnectOpen}
        onClose={handleDisconnectClose}
      />
    </div>
  );
}
